import React, { useEffect, useRef, useState } from 'react';
import { View } from 'react-native';
import Head from 'expo-router/head';
import { API_HOST } from '@/constants/config';
import AuthPage from '@/components/Pages/AuthPage';
import MetaHead from '@/components/MetaHead';
import Text from '@/components/Typography/Text';
import Link from '@/components/Typography/Link';
import Button from '@/components/Buttons/Button';
import StatusContainer from '@/components/StatusContainer';
import MfaForm from '@/components/Forms/MfaForm';
import SSOButtons from './SSOButtons';
import { createDesktopSignInController } from '@/lib/desktopSignIn';

async function request(path, { body, token } = {}) {
  const controller = new AbortController(), timer = setTimeout(() => controller.abort(), 20000);
  try {
    const response = await fetch(`${API_HOST}${path}`, { method: 'POST', credentials: 'omit', signal: controller.signal,
      headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) }, body: JSON.stringify(body) });
    const data = await response.json();
    if (!response.ok || data.error) throw new Error(data.message || 'Sign-in could not finish. Start again in Windows.');
    return data;
  } finally { clearTimeout(timer); }
}

export default function DesktopSignIn({ id }) {
  const [provider, setProvider] = useState(null), [error, setError] = useState(''), [pending, setPending] = useState(false);
  const [ticket, setTicket] = useState(null), [user, setUser] = useState(null), [done, setDone] = useState(false), [expired, setExpired] = useState(false);
  const flow = useRef(null), busy = useRef(false), expiry = useRef(null);
  useEffect(() => {
    let live = true, timer;
    setProvider(null); setError(''); setTicket(null); setUser(null); setDone(false); setExpired(false); busy.current = false; setPending(false);
    let controller;
    try { controller = createDesktopSignInController({ id, request, isCurrent: () => live }); flow.current = controller; }
    catch (e) { setError(e.message); return () => { live = false; }; }
    request('/json/auth/desktop/request', { body: { id } }).then(data => {
      if (!live) return;
      if (!['google', 'apple'].includes(data.provider) || !Number.isInteger(data.expiresIn) || data.expiresIn <= 0 || data.expiresIn > 300) throw new Error('This sign-in request has expired. Start again in Windows.');
      setProvider(data.provider);
      timer = setTimeout(() => { if (!live) return; setExpired(true); setError('This sign-in request has expired. Start again in Windows.'); void controller.dispose(); }, data.expiresIn * 1000);
      expiry.current = timer;
    }).catch(e => { if (live) setError(e.message); });
    return () => { live = false; clearTimeout(timer); if (flow.current === controller) flow.current = null; void controller.dispose(); };
  }, [id]);
  async function run(operation) {
    if (busy.current || !flow.current || expired) return;
    const controller = flow.current;
    busy.current = true; setPending(true); setError('');
    try {
      const result = await operation(controller);
      if (flow.current !== controller) return;
      if (result?.ticket) setTicket(result.ticket);
      if (result?.user) { setUser(result.user); setTicket(null); }
      if (result?.success) { clearTimeout(expiry.current); setDone(true); }
    } catch (e) { if (flow.current === controller) setError(e.message); }
    finally { if (flow.current === controller) { busy.current = false; setPending(false); } }
  }
  // Keep the owner captured when the MFA form rendered. Its network request can
  // finish after expiry/navigation; the retired controller must revoke it.
  const mfaOwner = flow.current;
  async function finishMfa(owner, data) {
    if (!owner) return;
    try {
      const result = await owner.completeMfa(data);
      if (flow.current === owner) { setUser(result.user); setTicket(null); }
    } catch (e) { if (flow.current === owner && !expired) setError(e.message); }
  }
  return <AuthPage title={done ? 'You’re signed in to Windows' : 'Sign in to Gab AI for Windows'}
    subtitle={done ? 'Return to the Windows app. You can close this tab.' : 'Continue with the account you want to use in the Windows app.'}>
    <MetaHead noindex title="Windows sign-in" />
    <Head><meta name="referrer" content="no-referrer" /></Head>
    <View style={{ gap: 16 }}>
      {!!error && <StatusContainer type="error" message={error} />}
      {!done && !expired && <>
        {!provider && !error && <Text>Checking your sign-in request…</Text>}
        {!!provider && !ticket && !user && <>
          <SSOButtons providers={[provider]} redirectAfterSuccess={false} onIdentity={input => run(controller => controller.identity(input))} />
          <Text size="s">By continuing, you agree to the <Link href="/terms-of-service">Terms of Service</Link> and <Link href="/privacy-policy">Privacy Policy</Link>.</Text>
        </>}
        {!!ticket && <MfaForm ticket={ticket} onAuthenticated={data => finishMfa(mfaOwner, data)}
          onRestart={() => { if (!pending) { setTicket(null); setUser(null); setError(''); } }} />}
        {!!user && <>
          <Text>Sign in to Windows as {user.email || user.fullName || 'this account'}?</Text>
          <Text size="s">Only confirm if you started this request in your own Gab AI Windows app.</Text>
          <Button text="Sign in to Windows" onPress={() => run(controller => controller.confirm())} isDisabled={pending} isLoading={pending} />
          <Button text="Use another account" variant="secondary" isDisabled={pending} onPress={() => { setUser(null); setError(''); }} />
        </>}
      </>}
    </View>
  </AuthPage>;
}
